import { Call, Device } from '@twilio/voice-sdk';
import {
  AgentAvailabilityStatus,
  AgentDirectoryEntry,
  CallDirection,
  CallLog,
  CallPurpose,
  Conference,
  SimplifiedMember,
} from '../../interfaces/calls';

export enum CallStatus {
  Idle = 'idle',
  Ringing = 'ringing',
  Calling = 'calling',
  OnCall = 'on_call',
  Transferring = 'transferring',
  Muted = 'muted',
  OnHold = 'on_hold',
  Ending = 'ending',
  Ended = 'ended',
  Failed = 'failed',
  Rejected = 'rejected',
  Reconnecting = 'reconnecting',
  Reconnected = 'reconnected',
}

export interface CallState {
  call: Call | null;
  status: CallStatus;
  direction: CallDirection | null;
  startTime?: Date;
  endTime?: Date;
  muted: boolean;
  onHold: boolean;
}

export interface TransferCallData {
  transferCallLogId: string;
  transferType: 'WARM' | 'BLIND';
  status: 'initiated' | 'ringing' | 'connected' | 'completed' | 'failed';
  targetAgent: {
    id: number;
    name: string;
    coreProviderId: number;
  };
  participantCallSid: string;
}

/**
 * Outcome of loading the signed-in user's agent record.
 *
 * `error` and `not-configured` were previously indistinguishable — both left
 * `agent` null, which silently hid the entire phone widget.
 */
export type AgentBootstrapState = 'loading' | 'ready' | 'error' | 'not-configured';

export interface PhoneCallsContextType {
  // Agent Management
  agent: Omit<AgentDirectoryEntry, 'status'> | null;
  agentAvailability: {
    previousStatus: AgentAvailabilityStatus;
    currentStatus: AgentAvailabilityStatus;
  };
  agentBootstrapState: AgentBootstrapState;
  retryAgentBootstrap: () => void;
  updateAgentAvailability: ({ status }: { status: AgentAvailabilityStatus }) => Promise<void>;

  // Device Management (for outgoing calls and general device management)
  device: Device | null;
  isDeviceOn: boolean;
  isDeviceLockedInOtherTab: boolean;
  toggleDevice: () => Promise<void>;
  activeCallState: CallState;

  // Active Call Management
  currentCallSid: string | null;
  conference: Conference | null;

  // Call State Computed Properties
  callDuration: number;
  isMuted: boolean;
  isOnHold: boolean;
  callLog: CallLog | null;

  // Rating State Management
  isAwaitingRating: boolean;
  audioRating: number | null;
  isSavingRating: boolean;
  setAwaitingRating: (awaiting: boolean) => void;
  setAudioRating: (rating: number | null) => void;

  // Call Purpose Management
  callPurpose: CallPurpose;
  setCallPurpose: (purpose: CallPurpose) => void;

  // Notes Management
  callNotes: string;
  setCallNotes: (notes: string) => void;
  saveCallSummary: () => Promise<void>;

  // Incoming Call Management
  isAgentReceivingCall: boolean;
  incomingCallType: 'member' | 'transfer' | 'agent';

  // Modal States
  isOutgoingCallModalOpen: boolean;
  isIncomingCallModalOpen: boolean;
  isDirectoryOpen: boolean;
  setIsDirectoryOpen: (open: boolean) => void;

  // Member Management
  selectedMember: SimplifiedMember | null;
  setSelectedMember: (member: SimplifiedMember | null) => void;

  // Phone Number Management
  memberPhoneNumber: string;
  setMemberPhoneNumber: (phoneNumber: string) => void;

  // Transfer Management
  transferData: TransferCallData | null;
  agentDirectory: AgentDirectoryEntry[];
  agentDirectoryLastUpdatedAt: Date | null;
  canInitiateTransfer: boolean;
  /** Resolves to whether the refresh succeeded, so callers can back off. */
  refreshAgentDirectory: () => Promise<boolean>;

  // Call Actions
  startOutgoingCall: (memberPhoneNumber: string, callPurpose?: string) => Promise<Call | null>;
  answerIncomingCall: (call: Call) => void;
  rejectIncomingCall: (call: Call) => void;
  hangUpCall: () => Promise<void>;
  muteCall: (muted: boolean) => void;
  holdUnholdConference: (hold: boolean, participantPhone: string) => Promise<void>;
  sendDigitsOnCall: (digits: string) => void;

  // Transfer Actions
  initiateWarmTransfer: (targetAgent: AgentDirectoryEntry) => Promise<void>;
  initiateBlindTransfer: (targetAgent: AgentDirectoryEntry) => Promise<void>;
  cancelTransfer: () => Promise<void>;

  // Device Management
  updateActiveCallState: (updates: Partial<CallState>) => void;

  // Modal Management
  openOutgoingCallModal: () => void;
  closeOutgoingCallModal: () => void;
  openIncomingCallModal: () => void;
  closeIncomingCallModal: () => void;

  // Navigation
  returnToCall: boolean;
  toggleReturnToCall: () => void;

  // Utility
  updateCurrentCallSid: (callId: string | null) => void;
  clearCallData: () => void;
}
